// PT-PT formatting for dates and times, presented in Europe/Lisbon.

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/pt";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("pt");

export const LISBON_TZ = "Europe/Lisbon";

const DTF_DATE = new Intl.DateTimeFormat("pt-PT", {
  timeZone: "Europe/Lisbon",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const DTF_TIME = new Intl.DateTimeFormat("pt-PT", {
  timeZone: "Europe/Lisbon",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const DTF_DATETIME = new Intl.DateTimeFormat("pt-PT", {
  timeZone: "Europe/Lisbon",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function parse(iso) {
  if (!iso) return null;
  if (iso instanceof Date) return iso;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function fmtDate(iso) {
  const d = parse(iso);
  return d ? DTF_DATE.format(d) : "—";
}

/**
 * fmtCode — apresentação simplificada do código do pedido e da referência de
 * material. O identificador interno mantém-se (ex.: "TF-2026-00325"); aqui
 * apenas simplificamos a apresentação:
 *   - Pedido:   "TF-2026-00237"      -> "TF-237"
 *   - Material: "TF-2026-00325-M01"  -> "TF-325-M01"
 * Qualquer outro formato é devolvido tal como está (retrocompatível).
 */
export function fmtCode(code) {
  if (!code || typeof code !== "string") return code || "";
  const m = code.match(/^TF-\d{4}-0*(\d+)(-.+)?$/i);
  return m ? `TF-${m[1]}${m[2] || ""}` : code;
}

// Alias explícito para referências de material (mesma lógica).
export function fmtRef(ref) {
  return fmtCode(ref);
}

/**
 * fmtAddress — apresentação legível de morada de cliente.
 * Aceita: ausência (null/undefined), string legacy, ou objeto estruturado
 * `{label, address, postal_code, city}` (client_address_snapshot). Nunca
 * devolve um objeto — só string, para nunca ser renderizado cru em JSX.
 * Ex.: "Fábrica · Rua de Cima 12, 4785-123 Trofa".
 */
export function fmtAddress(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (typeof addr !== "object") return "";
  const { label, address, postal_code, city } = addr;
  const locality = [postal_code, city].filter(Boolean).join(" ");
  const line = [address, locality].filter(Boolean).join(", ");
  return label && line ? `${label} · ${line}` : label || line || "";
}

export function fmtTime(iso) {
  const d = parse(iso);
  return d ? DTF_TIME.format(d) : "—";
}

export function fmtDateTime(iso) {
  const d = parse(iso);
  return d ? DTF_DATETIME.format(d) : "—";
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase 5.4 — conversão partilhada entre <input type="datetime-local"> e ISO
// 8601, usada por PedidoNovo e EditRequestDialog para prazo e lembrete.
// O browser interpreta o valor do input na hora local do sistema; a app
// assume que essa hora local é Europe/Lisbon (mesma assunção já usada antes
// nos dois formulários, agora apenas centralizada).
// ─────────────────────────────────────────────────────────────────────────────
export function toDateTimeLocalInput(isoOrDate) {
  if (!isoOrDate) return "";
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromDateTimeLocalInput(value) {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function fmtRoleLabel(role) {
  switch (role) {
    case "admin":
      return "Administrador";
    case "gestor":
      return "Gestor";
    case "operador":
      return "Operador";
    default:
      return role;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Fase 3 — tempo relativo em PT-PT natural para "Última atividade".
// Escala:
//   < 60s               → "Agora"
//   < 60m               → "Há X minuto(s)"
//   < 24h (mesmo dia)   → "Há X hora(s)"
//   ontem               → "Ontem às HH:MM"
//   < 7 dias            → "Há X dias"
//   ≥ 7 dias            → "Há X dias" (a badge de inatividade complementa)
// Devolve "—" para valores inválidos.
// ─────────────────────────────────────────────────────────────────────────────
export function fmtRelativeTime(iso) {
  const d = parse(iso);
  if (!d) return "—";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);

  if (diffSec < 45) return "Agora";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `Há ${diffMin} minuto${diffMin === 1 ? "" : "s"}`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `Há ${diffHour} hora${diffHour === 1 ? "" : "s"}`;

  // Verificar se foi "ontem" (mesmo calendário local Lisboa via toDateString).
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 24 * 3600 * 1000);
  if (d >= startOfYesterday && d < startOfToday) {
    return `Ontem às ${DTF_TIME.format(d)}`;
  }

  const diffDay = Math.floor((startOfToday.getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / (24 * 3600 * 1000));
  return `Há ${diffDay} dia${diffDay === 1 ? "" : "s"}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// M2 — variante curta do tempo relativo, para faixas compactas em mobile,
// onde a data completa ("27/09/2026, 16:32") rouba uma linha inteira ao
// conteúdo. Mesma escala de `fmtRelativeTime`, escrita para caber:
//   "agora" · "há 5 min" · "há 2 h" · "há 3 dias"
// Em minúsculas porque entra no meio de uma frase ("Atualizado há 5 min").
// ─────────────────────────────────────────────────────────────────────────────
export function fmtRelativeShort(iso) {
  const d = parse(iso);
  if (!d) return "—";
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 45) return "agora";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `há ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  return `há ${diffDay} dia${diffDay === 1 ? "" : "s"}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Bloco F1 — limites de semana em Europe/Lisbon, convertidos para UTC.
// Semana começa segunda-feira 00:00 (hora de Lisboa) e termina no domingo
// seguinte às 24:00 (== segunda seguinte 00:00), respeitando DST (o offset
// de Lisboa muda no último domingo de março/outubro).
// ─────────────────────────────────────────────────────────────────────────────
// dayjs (plugin utc/timezone) preserva o instante UTC correto em `.add()`/
// `.subtract()`, mas não recalcula o offset de Lisboa quando a operação
// atravessa uma transição de DST — o objeto resultante fica com o offset
// "antigo" (ex.: somar 7 dias a partir de antes do último-domingo-de-março
// devolve um instante 1h errado). Reformatar a hora local e reanalisar com
// `dayjs.tz` força o recálculo correto do offset para essa data/hora.
function relocalizeLisbon(d) {
  return dayjs.tz(d.format("YYYY-MM-DD HH:mm:ss.SSS"), LISBON_TZ);
}

export function startOfWeekLisbon(referenceDate = new Date()) {
  const ref = dayjs(referenceDate).tz(LISBON_TZ);
  const dow = ref.day(); // 0=domingo..6=sábado
  const diffToMonday = dow === 0 ? 6 : dow - 1;
  return relocalizeLisbon(ref.subtract(diffToMonday, "day")).startOf("day");
}

export function endOfWeekLisbon(referenceDate = new Date()) {
  return relocalizeLisbon(startOfWeekLisbon(referenceDate).add(7, "day"));
}

export function buildWeekWindowUTC(referenceDate = new Date()) {
  const startLocal = startOfWeekLisbon(referenceDate);
  const endLocal = relocalizeLisbon(startLocal.add(7, "day"));
  return {
    fromISO: startLocal.utc().toISOString(),
    toISO: endLocal.utc().toISOString(),
    startLocal,
    endLocal,
  };
}

export function shiftWeek(referenceDate, deltaWeeks) {
  return relocalizeLisbon(dayjs(referenceDate).tz(LISBON_TZ).add(deltaWeeks * 7, "day")).toDate();
}

// Exportado para reutilização em agenda.js (segmentação diária/view-model
// da semana), que sofre do mesmo problema ao somar dias em Lisboa.
export { relocalizeLisbon };

export function dayKeyLisbon(isoOrDayjs) {
  return dayjs(isoOrDayjs).tz(LISBON_TZ).format("YYYY-MM-DD");
}
